/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.audio_fe

import io.peekandpoke.klang.audio_bridge.AudioContext
import io.peekandpoke.klang.audio_bridge.MonoSamplePcm
import io.peekandpoke.klang.audio_fe.samples.AudioDecoder
import io.peekandpoke.klang.common.logging.LogArea
import io.peekandpoke.klang.common.logging.Logger
import kotlinx.coroutines.await
import org.khronos.webgl.Float32Array
import org.khronos.webgl.Int8Array

private val log = Logger(area = LogArea.Engine, source = "BrowserAudioDecoder")

class BrowserAudioDecoder : AudioDecoder {
    // We need an AudioContext to decode, even if we don't play anything with it.
    // Ideally reuse the one from the player, but for decoding a transient one is fine/standard.
    private val defaultCtx: AudioContext by lazy { AudioContext() }

    override suspend fun decodeMonoFloatPcm(audioBytes: ByteArray): MonoSamplePcm? {
        return try {
            // 1. Convert Kotlin ByteArray to JS ArrayBuffer
            // We need to copy the bytes because Kotlin ByteArray might not align with JS Buffer view expectations directly here
            val int8Array = Int8Array(audioBytes.toTypedArray())
            val arrayBuffer = int8Array.buffer

            val ctx: AudioContext = try {
                js("new OfflineAudioContext(1, 1, 48000)")
            } catch (e: Throwable) {
                log.error("Failed to create OfflineAudioContext", e)
                // Use the default context
                defaultCtx
            }

            // 2. Decode (Async)
            // decodeAudioData returns a Promise<AudioBuffer>
            val audioBuffer = ctx.decodeAudioData(arrayBuffer).await()

            // 3. Extract Mono Data
            if (audioBuffer.numberOfChannels == 0) return null

            // getChannelData returns a Float32Array
            val pcmFloat32: Float32Array = audioBuffer.getChannelData(0)

            // 4. Return
            // The engine's canonical sample type is Double — widen Float32Array → DoubleArray here
            // (Kotlin/JS DoubleArray is backed by Float64Array). One-shot per decoded asset.
            val asFloatArray = pcmFloat32.unsafeCast<FloatArray>()
            MonoSamplePcm(
                sampleRate = audioBuffer.sampleRate,
                pcm = DoubleArray(asFloatArray.size) { asFloatArray[it].toDouble() }
            )
        } catch (e: Throwable) {
            log.error("Failed to decode audio", e)
            null
        }
    }
}
